// frontend/src/features/tanks/TankCard.tsx
// One tank on the Tanks page (design 6.4): its cover photo at 16:9, name and
// Selected badge, volume, a line of description, "Tank shots · N" and the
// Select, Edit and Delete actions.

import { useState } from 'react';
import {
  Anchor,
  AspectRatio,
  Badge,
  Button,
  Card,
  Center,
  Group,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { IconCheck, IconContainer, IconEdit, IconPhotoPlus, IconTrash } from '@tabler/icons-react';
import { Link } from 'react-router-dom';
import type { Tank } from '@/types/tank';
import { tankShotsPath } from '@/features/livestock/TankShotsRow';
import classes from './TankCard.module.css';

export interface TankCardProps {
  tank: Tank;
  selected: boolean;
  onSelect: (tank: Tank) => void;
  onEdit: (tank: Tank) => void;
  onDelete: (tank: Tank) => void;
  /** The cover failed to load (its signed URL may have expired); called once per URL. */
  onImageError?: () => void;
}

/** 44 px on a phone, the usual compact height from sm up. */
const ACTION_HEIGHT = { base: 44, sm: 30 };

export function TankCard({
  tank,
  selected,
  onSelect,
  onEdit,
  onDelete,
  onImageError,
}: TankCardProps): JSX.Element {
  const count = tank.tank_shot_count ?? 0;
  const coverUrl = tank.cover_display_url ?? tank.cover_thumb_url ?? null;
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const showCover = !!coverUrl && coverUrl !== failedUrl;
  const shotsPath = tankShotsPath(tank.id);

  return (
    <Card withBorder padding="md" h="100%" data-testid={`tank-card-${tank.id}`}>
      <Card.Section>
        <AspectRatio ratio={16 / 9}>
          {showCover ? (
            <Link to={shotsPath} className={classes.coverLink}>
              <img
                src={coverUrl ?? undefined}
                alt={`${tank.name}: open its tank shots`}
                loading="lazy"
                decoding="async"
                className={classes.cover}
                onError={() => {
                  setFailedUrl(coverUrl);
                  onImageError?.();
                }}
              />
            </Link>
          ) : count > 0 ? (
            // Shots but no URL yet (the tanks list is being refetched): no invitation.
            <Center className={classes.noCover}>
              <IconContainer size={32} color="var(--mantine-color-gray-5)" />
            </Center>
          ) : (
            <Center className={classes.noCover}>
              <Stack align="center" gap={6}>
                <IconContainer size={32} color="var(--mantine-color-teal-6)" />
                <Anchor
                  component={Link}
                  to={shotsPath}
                  size="sm"
                  fw={500}
                  c="var(--rdh-brand-on-light)"
                  className={classes.addShot}
                >
                  <Group gap={4} wrap="nowrap">
                    <IconPhotoPlus size={16} />
                    Add a tank shot
                  </Group>
                </Anchor>
              </Stack>
            </Center>
          )}
        </AspectRatio>
      </Card.Section>

      <Stack gap={6} mt="sm">
        <Group gap="xs" wrap="nowrap" justify="space-between">
          <Title order={3} fz={18} fw={600} lineClamp={1}>
            {tank.name}
          </Title>
          {selected && (
            <Badge size="sm" color="teal" variant="light" leftSection={<IconCheck size={12} />}>
              Selected
            </Badge>
          )}
        </Group>
        <Text size="sm" c="dimmed" style={{ fontVariantNumeric: 'tabular-nums' }}>
          {tank.volume != null ? `${tank.volume} ${tank.volume_unit || ''}`.trim() : 'No volume set'}
        </Text>
        <Text size="sm" c="dimmed" lineClamp={1}>
          {tank.description || 'No description'}
        </Text>
        {count > 0 && (
          <Anchor
            component={Link}
            to={shotsPath}
            size="sm"
            fw={500}
            c="var(--rdh-brand-on-light)"
            className={classes.shotsLink}
          >
            {`Tank shots · ${count}`}
          </Anchor>
        )}
      </Stack>

      {/* Pushed to the foot, so the actions line up across a row of cards. */}
      <Group gap="xs" mt="auto" pt="sm" wrap="wrap">
        {!selected && (
          <Button
            variant="light"
            size="xs"
            h={ACTION_HEIGHT}
            onClick={() => onSelect(tank)}
            aria-label={`Select ${tank.name}`}
          >
            Select
          </Button>
        )}
        <Button
          variant="subtle"
          size="xs"
          h={ACTION_HEIGHT}
          leftSection={<IconEdit size={16} />}
          onClick={() => onEdit(tank)}
          aria-label={`Edit ${tank.name}`}
        >
          Edit
        </Button>
        <Button
          variant="subtle"
          size="xs"
          h={ACTION_HEIGHT}
          color="red"
          leftSection={<IconTrash size={16} />}
          onClick={() => onDelete(tank)}
          aria-label={`Delete ${tank.name}`}
        >
          Delete
        </Button>
      </Group>
    </Card>
  );
}
